import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowsClockwiseIcon,
    CaretRightIcon,
    NotePencilIcon,
    DotsThreeIcon,
    LinkBreakIcon,
} from '@phosphor-icons/react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { DropdownMenuItem } from '@/components/DropdownMenu/DropdownMenuItem';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface EventEntityMenuProps {
    replaceContent: (onClose: () => void) => ReactNode;
    onUnlink: () => void;
    onEdit: () => void;
    isUnknown?: boolean;
}

export function EventEntityMenu({
    replaceContent,
    onUnlink,
    onEdit,
    isUnknown = false,
}: EventEntityMenuProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const [open, setOpen] = useState(false);

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    aria-label={t('eventView.entityMenu.openMenu')}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-grey-200 outline-none hover:bg-grey-500 hover:text-white-100 focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900"
                >
                    <DotsThreeIcon className="size-5" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={4}
                variant="compact"
                className="min-w-[260px]"
            >
                <DropdownMenuLabel>
                    {t('eventView.entityMenu.sections.event')}
                </DropdownMenuLabel>
                <DropdownMenuItem
                    icon={ArrowsClockwiseIcon}
                    label={t('eventView.entityMenu.replaceLinkedEntity')}
                    trailingBadge={
                        <CaretRightIcon
                            className="size-4 text-grey-200 group-hover:text-white"
                            aria-hidden
                        />
                    }
                >
                    {replaceContent(() => setOpen(false))}
                </DropdownMenuItem>
                <DropdownMenuItem
                    icon={LinkBreakIcon}
                    label={t('eventView.entityMenu.unlinkFromEvent')}
                    onClick={onUnlink}
                />
                <DropdownMenuLabel>
                    {t('eventView.entityMenu.sections.entity')}
                </DropdownMenuLabel>
                <DropdownMenuItem
                    icon={NotePencilIcon}
                    label={
                        isUnknown
                            ? t('eventView.entityMenu.markAsKnown')
                            : t('eventView.entityMenu.editEntityDetails')
                    }
                    onClick={onEdit}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
